/**
 * Look-ahead: a private copy of a running cave that the bot steps with the real engine. The world
 * is fully determined by the state (its `Rng` included) and Digby's input, so a copy stepped with
 * the same inputs shows exactly what will happen. The real state is only ever read.
 */

import { Rng, step } from '../sim';
import type { CaveState, Input } from '../sim';

function blank(like: CaveState): CaveState {
  return {
    ...like,
    cells: new Uint8Array(like.cells.length),
    stamp: new Uint32Array(like.stamp.length),
    moved: new Uint8Array(like.moved.length),
    rng: new Rng(0),
    digby: { ...like.digby },
    amoeba: { ...like.amoeba },
    magic: { ...like.magic },
    keys: [...like.keys],
    events: [],
  };
}

export class Sandbox {
  private s: CaveState;
  /** Scans simulated since the budget was last reset. */
  steps = 0;

  constructor(like: CaveState) {
    this.s = blank(like);
  }

  get state(): CaveState {
    return this.s;
  }

  /** Become a copy of `src` (reusing the buffers when the cave is the same shape). */
  load(src: CaveState): void {
    const d = this.s;
    if (d.w !== src.w || d.h !== src.h || d.params !== src.params || d.keys.length !== src.keys.length) {
      this.s = blank(src);
    }
    const t = this.s;
    t.cells.set(src.cells);
    // Stamps must match: a stale stamp equal to a future scan number would freeze that cell.
    t.stamp.set(src.stamp);
    // Rng keeps its state in plain fields, so copying them clones the generator exactly.
    t.rng.copyFrom(src.rng);
    Object.assign(t.digby, src.digby);
    Object.assign(t.amoeba, src.amoeba);
    Object.assign(t.magic, src.magic);
    for (let i = 0; i < src.keys.length; i++) t.keys[i] = src.keys[i] ?? false;
    t.scan = src.scan;
    t.ms = src.ms;
    t.bornMs = src.bornMs;
    t.hatchScan = src.hatchScan;
    t.timeLeft = src.timeLeft;
    t.secondMs = src.secondMs;
    t.diamonds = src.diamonds;
    t.score = src.score;
    t.gateOpen = src.gateOpen;
    t.status = src.status;
    t.events.length = 0;
  }

  run(input: Input): void {
    step(this.s, input);
    this.steps++;
  }

  dead(): boolean {
    return !this.s.digby.alive || this.s.status === 'dead';
  }

  won(): boolean {
    return this.s.status === 'won';
  }
}
